import classNames from 'classnames';
import { ThemeWBProps } from 'eco-design-micro/props/const';
import { DataLayerProps } from 'eco-design-micro/props/DataLayerProps';
import gtm from 'eco-design-reliance/gtm';
import EcoLink from 'eco-design/base/eco-link';
import React, { Fragment, useEffect, useState } from 'react';
import IconArrow from './comps/IconArrow';
import { saveAICookieForShopify } from 'eco-design-micro/common/CookieForShopify';
import getLocal from 'eco-design-reliance/getLocal';

import './index.scss';

export interface EcoNavProductItemProps {
    /**
     * key
     */
    value?: string;
    /**
     * label 名称
     */
    label?: string;
    /**
     * 链接
     */
    href?: string;
    /**
     * 阻止高亮
     */
    disableTabHighlight?: boolean;
    /**
     * 自定义埋点
     */
    dataLayer?: DataLayerProps;
}
export interface EcoNavProductProps {
    /**
     * 默认选择tab
     */
    selectType?: string;
    /**
     * 主题色
     */
    theme?: ThemeWBProps;
    /**
     * 选中的 product
     */
    defaultProductActiveKey?: string;
    /**
     * 产品列表
     */
    productItems?: EcoNavProductItemProps[];
    /**
     * buy 配置
     */
    buyItems?: EcoNavProductItemProps[];
    /**
     * 选中的 tab
     */
    defaultTabActiveKey?: string;
    /**
     * tab列表
     */
    tabItems?: EcoNavProductItemProps[];
    /**
     * 背景色
     */
    backgroundColor?: string;
    /**
     * 产品名颜色
     */
    productNameColor?: string;
    /**
     * 产品名颜色-激活
     */
    productNameActiveColor?: string;
    /**
     * tab选项颜色
     */
    tabNameColor?: string;
    /**
     * tab选项颜色-激活
     */
    tabNameActiveColor?: string;
    /**
     * buy字体色
     */
    buyFontColor?: string;
    /**
     * buy背景色
     */
    buyBgColor?: string;
    /**
     * 样式名
     */
    className?: string;
    /**
     * 子产品 切换
     */
    onProductChange?: (item: EcoNavProductItemProps, index: number) => void;
    /**
     * tab 切换
     */
    onTabChange?: (item: EcoNavProductItemProps, index: number) => void;
    /**
     * 点击 buy 切换
     */
    onBuyChange?: (item: EcoNavProductItemProps, index: number) => void;
    /**
     * 埋点-tab切换
     */
    dataLayerTab?: DataLayerProps;
    /**
     * 埋点-buy
     */
    dataLayerBuy?: DataLayerProps;
    /**
     * 开启PC端的多个下拉选项
     */
    openMoreSelectInPc?: boolean;
}

// 根据 value 获取item
const getSelectItemFromKey = (items: EcoNavProductItemProps[], activeValue: string) => {
    const items2 = items || [];
    for (let i = 0; i < items2.length; i++) {
        const item = items2[i];
        if (item.value === activeValue) {
            return item;
        }
    }

    return items2[0] || {};
};
/**
 * 维护:谢保林
 *
 * 产品导航
 */
const EcoNavProduct: React.FC<EcoNavProductProps> = ({
    defaultProductActiveKey = '',
    defaultTabActiveKey = '',
    backgroundColor,
    productNameColor,
    productNameActiveColor,
    tabNameColor,
    tabNameActiveColor,
    buyFontColor,
    buyBgColor,
    theme,
    buyItems = [],
    productItems = [],
    tabItems = [],
    className,
    onProductChange,
    onTabChange,
    onBuyChange,
    dataLayerTab,
    dataLayerBuy,
    openMoreSelectInPc = false,
}) => {
    const prefixCls = `eco-nav-product`;
    const productItems2 = productItems || [];
    const openProductMore = productItems2.length > 1;
    const [openProductSelect, setOpenProductSelect] = useState(false); //是否打开 select
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-openMoreSelectInPc`]: openMoreSelectInPc,
        [`${prefixCls}-product-more`]: openProductMore,
        [`${prefixCls}-product-open`]: openProductSelect,
    });
    // 选中的 product
    const defaultProductItem = getSelectItemFromKey(productItems2, defaultProductActiveKey);
    const [activeProductItem, setActiveProductItem] = useState<EcoNavProductItemProps>(defaultProductItem);
    //const activeProductItem = defaultProductItem;
    // 选中的 tab
    const defaultTabItem = getSelectItemFromKey(tabItems, defaultTabActiveKey);
    const [activeTabItem, setActiveTabItem] = useState<EcoNavProductItemProps>(defaultTabItem);
    const { locale = 'us' } = getLocal();

    // tab 默认埋点
    const dataLayer_tab_default = dataLayerTab || {
        event: 'uaEvent',
        event_name: 'product_navigation',
        product_name: activeProductItem.label,
    };
    // buy 默认埋点
    const dataLayer_shopify_buy = dataLayerBuy || {
        event: 'uaEvent',
        event_name: 'buy',
        product_name: activeProductItem.label,
        position: 'Product Details Navigation',
    };

    const dataLayer_shopify_checkout = dataLayerBuy || {
        event: 'uaEvent',
        event_name: 'checkout',
        button_name: '$buttonName_$productName',
    };
    // 切换 name
    const toggleNameHandleA = () => {
        if (!openProductMore) {
            return false;
        }
        const openProductSelect2 = !openProductSelect;
        setOpenProductSelect(openProductSelect2);
    };

    // tab 切换
    const tabHandle = (item: EcoNavProductItemProps, index: number) => {
        if (!item.disableTabHighlight) setActiveTabItem(item);
        if (onTabChange) {
            onTabChange(item, index);
        }
        try {
            if (item.dataLayer) {
                gtm.push(item.dataLayer);
            } else {
                gtm.push({
                    ...dataLayer_tab_default,
                    navigation_name: item.label,
                });
            }
        } catch (error) {}
    };
    // 点击buy
    const buyHandle = (item: EcoNavProductItemProps, index: number) => {
        if (onBuyChange) {
            onBuyChange(item, index);
        }
        const buyHref = item.href || '';
        if (buyHref.includes('/products/')) {
            try {
                saveAICookieForShopify(buyHref, locale);
                gtm.push(dataLayer_shopify_buy);
            } catch (error) {
                /* empty */
            }
        } else if (buyHref.includes('/checkouts/')) {
            try {
                gtm.push({
                    ...dataLayer_shopify_checkout,
                    button_name: `${item.label}_${activeProductItem.label}`,
                });
            } catch (error) {
                /* empty */
            }
        }
    };
    // 点击产品切换
    const productChangeHandle = (item: EcoNavProductItemProps, index: number) => {
        if (onProductChange) {
            onProductChange(item, index);
        }
        setOpenProductSelect(false);
        setActiveProductItem(item);
    };

    useEffect(() => {
        setActiveTabItem(getSelectItemFromKey(tabItems, defaultTabActiveKey));
    }, [defaultTabActiveKey]);

    const compStyle = {
        '--eco-nav-product-name-color': productNameColor,
        '--eco-nav-product-name-color-active': productNameActiveColor,
        '--eco-nav-product-tab-item-color': tabNameColor,
        '--eco-nav-product-tab-item-color-active': tabNameActiveColor,
        '--eco-nav-background': backgroundColor,
        '--eco-nav-buy-font-color': buyFontColor,
        '--eco-nav-buy-bg-color': buyBgColor,
    } as React.CSSProperties;
    return (
        <div className={classes} id="global-nav-product" style={compStyle}>
            <div className={`${prefixCls}-in`}>
                <div className={`${prefixCls}-name`}>
                    <div className={`${prefixCls}-name-select`} onClick={toggleNameHandleA}>
                        {activeProductItem.label}
                        {openProductMore && <IconArrow />}
                    </div>
                    <div className={`${prefixCls}-name-items`}>
                        {productItems2.map((item: EcoNavProductItemProps, index: number) => {
                            return (
                                <Fragment key={index}>
                                    {index > 0 && <span className={`${prefixCls}-name-item-border`}></span>}
                                    <EcoLink
                                        href={item.href}
                                        onClick={() => {
                                            productChangeHandle(item, index);
                                        }}
                                        dataLayer={
                                            item.dataLayer
                                                ? item.dataLayer
                                                : {
                                                      ...dataLayer_tab_default,
                                                      product_name: item.label,
                                                      navigation_name: item.label,
                                                  }
                                        }
                                        className={`${prefixCls}-name-item${activeProductItem.value === item.value ? ` ${prefixCls}-name-item-active` : ''}`}
                                    >
                                        {item.label}
                                    </EcoLink>
                                </Fragment>
                            );
                        })}
                    </div>
                </div>
                <div className={`${prefixCls}-tab`}>
                    <div className={`${prefixCls}-tab-in`}>
                        {tabItems?.map((item: EcoNavProductItemProps, index: number) => {
                            if (!item.label) {
                                return null;
                            }
                            return (
                                <EcoLink
                                    className={`${prefixCls}-tab-item${activeTabItem.value === item.value ? ` ${prefixCls}-tab-item-active` : ''}`}
                                    href={item.href}
                                    key={index}
                                    onClick={() => {
                                        tabHandle(item, index);
                                    }}
                                >
                                    {item.label}
                                </EcoLink>
                            );
                        })}
                    </div>
                </div>
                {buyItems.length > 0 && (
                    <div className={`${prefixCls}-buy`}>
                        {buyItems.map((item: EcoNavProductItemProps, index: number) => {
                            return (
                                <EcoLink
                                    className={`${prefixCls}-buy-item`}
                                    href={item.href}
                                    key={index}
                                    onClick={() => {
                                        buyHandle(item, index);
                                    }}
                                >
                                    {item.label}
                                </EcoLink>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EcoNavProduct;
